import { Operator } from '@elementary/common-ui/hooks/useUrlFilters';

export type Token = {
  id: string;
  description: string;
  createdAt: string;
  expires: string;
};

export type CreateTokenRequest = {
  description: string;
  expiresInMinutes?: number;
  roleIds?: string[];
};

export type CreateTokenResponse = {
  id: string;
  description: string;
  createdAt: string;
  secret: string;
  expires: string;
};

export type GetTokensResponse = {
  accessTokens: Token[];
};

export type TokenApi<T extends CreateTokenRequest = CreateTokenRequest> = {
  getTokens: () => Promise<GetTokensResponse>;
  createToken: (data: T) => Promise<CreateTokenResponse>;
  deleteToken: (tokenId: string) => Promise<void>;
};

export type TokenType = 'personal' | 'account' | 'runtime';

export type TokenConfig = {
  queryKey: string[];
  api: TokenApi;
  tokenType: Exclude<TokenType, 'runtime'>;
  successMessages: {
    create: string;
    delete: string;
    createError: string;
    deleteError: string;
  };
};

export enum PermissionsAccess {
  HIDDEN = 'hidden',
  READ = 'read',
  WRITE = 'write',
  CUSTOM = 'custom',
}

export enum AccountLevelPermission {
  NO_PERMISSIONS = 'no_permissions',
  FULL_MANAGEMENT = 'full_management',
}

export enum ResourceFilterProperty {
  ASSET_TAG = 'asset_tag',
  ASSET_OWNER = 'asset_owner',
  ASSET_SUBSCRIBER = 'asset_subscriber',
  ASSET_NAME = 'asset_name',
  ASSET_PATH = 'asset_path',
  ASSET_IS_CRITICAL = 'asset_is_critical',
  ASSET_DATABASE_NAME = 'asset_database_name',
  ASSET_SCHEMA_NAME = 'asset_schema_name',
  ASSET_TABLE_NAME = 'asset_table_name',
  TEST_TAG = 'test_tag',
  TEST_OWNER = 'test_owner',
  TEST_NAME = 'test_name',
}

export type ResourceFilter = {
  property: string;
  operator: Operator;
  value: string[];
};

export type ResourceFilterGroup = {
  access: PermissionsAccess;
  filters: ResourceFilter[];
};

export type ApiResourceFilter = {
  property: ResourceFilterProperty;
  operator: Operator;
  value: string[];
};

export type CustomFeaturesAccess = {
  assets?: PermissionsAccess;
  tests?: PermissionsAccess;
  incidents?: PermissionsAccess;
  alert_digest_rules?: PermissionsAccess;
  pull_requests?: PermissionsAccess;
  saved_views?: PermissionsAccess;
  ticketing?: PermissionsAccess;
};

export type EnvPermission = {
  environment_id: string;
  features_access: PermissionsAccess;
  custom_features_access?: CustomFeaturesAccess | null;
  default_resource_permission: PermissionsAccess;
};

export type ResourceGroupPermission = {
  environment_id: string;
  access: PermissionsAccess;
  resource_filters: ApiResourceFilter[];
};

export type CreateRoleRequest = {
  name: string;
  description?: string;
  account_level_permission: AccountLevelPermission;
  new_environment_default_access: PermissionsAccess;
  env_permissions: EnvPermission[];
  resource_group_permissions: ResourceGroupPermission[];
};

export type UpdateRoleRequest = {
  name?: string;
  description?: string;
  account_level_permission?: AccountLevelPermission;
  new_environment_default_access?: PermissionsAccess;
  env_permissions?: EnvPermission[];
  resource_group_permissions?: ResourceGroupPermission[];
};

export type RoleResponse = {
  id: string;
  account_id: string;
  name: string;
  description: string | null;
  account_level_permission: AccountLevelPermission;
  new_environment_default_access: PermissionsAccess;
  env_permissions: EnvPermission[];
  resource_group_permissions: ResourceGroupPermission[];
  created_at: string;
  updated_at: string;
  builtin: boolean;
};

export type Role = {
  id: string;
  name: string;
  description: string | null;
  builtin: boolean;
};

export type EnvPermissionFormData = {
  environmentId: string;
  featuresAccess: PermissionsAccess;
  customFeaturesAccess: CustomFeaturesAccess | null;
  defaultResourcePermission: PermissionsAccess;
  resourceFilterGroups: ResourceFilterGroup[];
};

export type RoleFormData = {
  name: string;
  description: string | null;
  accountLevelPermission: AccountLevelPermission;
  newEnvironmentDefaultAccess: PermissionsAccess;
  envPermissions: EnvPermissionFormData[];
};
